import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

export default function Register() {
    const [params] = useSearchParams();
    const defaultRole = params.get("role") === "creator" ? "creator" : "buyer";
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        company: "",
        role: defaultRole,
    });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const nav = useNavigate();

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await register(form);
            if (u.role === "creator") nav("/creator");
            else nav("/catalog");
        } catch (e) {
            setErr(e.message || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink-300">
            <Navbar />
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-5">
                        Apply for membership
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl leading-none">
                        Join the <span className="platinum-text">exchange.</span>
                    </h1>
                    <p className="mt-6 text-silver-400 max-w-md">
                        Pick a side — upload a film for assignment, or arrive as a verified
                        buyer hunting the next worldwide exclusive.
                    </p>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="glass p-8 md:p-10 rounded-sm"
                    data-testid="register-form"
                >
                    <div className="grid grid-cols-2 gap-2 mb-6">
                        {["buyer", "creator"].map((r) => (
                            <button
                                type="button"
                                key={r}
                                onClick={() => setForm({ ...form, role: r })}
                                className={`py-3 text-sm tracking-widest uppercase rounded-sm border transition-colors ${
                                    form.role === r
                                        ? "border-platinum text-platinum bg-platinum/5"
                                        : "border-white/10 text-silver-400 hover:text-white"
                                }`}
                                data-testid={`register-role-${r}`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>

                    <div className="space-y-5">
                        <Field label="Full name" value={form.name} onChange={set("name")} testid="register-name" required />
                        <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="register-email" required />
                        <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="register-password" required />
                        <Field
                            label={form.role === "creator" ? "Production company" : "Studio / Company"}
                            value={form.company}
                            onChange={set("company")}
                            testid="register-company"
                        />
                        {err && (
                            <div className="text-sm text-red-400" data-testid="register-error">
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 font-medium py-3 rounded-sm tracking-wider uppercase text-sm"
                            data-testid="register-submit"
                        >
                            {loading ? "Creating…" : "Create Account"}
                        </button>
                        <div className="text-sm text-silver-400 text-center">
                            Already have an account?{" "}
                            <Link to="/login" className="text-platinum hover:underline">
                                Sign in
                            </Link>
                        </div>
                    </div>
                </motion.form>
            </div>
        </div>
    );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-widest text-silver-400 mb-2">
                {label}
            </label>
            <input
                type={type}
                required={required}
                value={value}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/20 focus:border-platinum focus:outline-none py-2 text-white"
                data-testid={testid}
            />
        </div>
    );
}
